import type { FC, FormEvent, ReactNode } from 'react'

import { useRef, useState } from 'react'
import Link from 'next/link'

import { gsap, useGsap } from '@/lib/gsap'
import { cn } from '@/lib/utils'
import { RollText } from '@/components/generals/roll-link'

type Status = 'idle' | 'sending' | 'done' | 'error'

const EMPTY = {
    name: '',
    company: '',
    service: '',
    pitch: '',
    budget: '',
    start: '',
    email: '',
    website: ''
}

/* Fields are rules, not boxes. An underline sits inside the sentence instead
   of interrupting it, which is what lets the whole form read as one line of
   copy the visitor completes rather than a stack of inputs. */
const field = 't-cond-soft min-w-0 border-b border-paper/30 bg-transparent pb-1.5 text-[clamp(15px,1.5vw,17px)] text-paper outline-none transition-colors duration-300 placeholder:text-ash focus:border-brand'

const label = 't-cond shrink-0 text-[clamp(15px,1.5vw,17px)] leading-none text-ash-soft'

interface EnquiryFormProps {
    className?: string
}

export const EnquiryForm: FC<EnquiryFormProps> = ({ className }): ReactNode => {
    const scope = useRef<HTMLFormElement>(null)
    const [values, setValues] = useState(EMPTY)
    const [status, setStatus] = useState<Status>('idle')
    const [message, setMessage] = useState('')

    const complete = values.name.trim().length > 1
        && values.pitch.trim().length > 9
        && values.email.includes('@')
        && values.start.length > 0

    useGsap(() => {
        const root = scope.current

        if (!root) {
            return
        }

        gsap.fromTo('[data-field]',
            { opacity: 0, y: 24 },
            {
                opacity: 1,
                y: 0,
                duration: 0.85,
                stagger: 0.07,
                ease: 'noma',
                scrollTrigger: { trigger: root, start: 'top 86%', once: true }
            }
        )
    }, scope, [])

    const set = (key: keyof typeof EMPTY) => (event: { target: { value: string } }): void => {
        setValues((current) => ({ ...current, [key]: event.target.value }))
    }

    const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        if (status === 'sending') {
            return
        }

        setStatus('sending')
        setMessage('')

        try {
            const response = await fetch('/api/enquiry', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(values)
            })

            const data = await response.json()

            if (!response.ok) {
                throw new Error(data?.message ?? 'Something went wrong.')
            }

            setStatus('done')
            setMessage(data.message ?? 'Thanks. You will hear back within a day.')
            setValues(EMPTY)
        } catch (error) {
            setStatus('error')
            setMessage(error instanceof Error ? error.message : 'Something went wrong.')
        }
    }

    return (
        <form ref={scope} onSubmit={onSubmit} className={cn('flex flex-col gap-9', className)}>
            <div data-field className='flex flex-wrap items-baseline gap-x-4 gap-y-3'>
                <label htmlFor='enq-name' className={label}>
                    My name is
                </label>

                <input
                    id='enq-name'
                    type='text'
                    name='name'
                    required
                    value={values.name}
                    onChange={set('name')}
                    placeholder='Name'
                    autoComplete='name'
                    className={cn(field, 'w-[min(100%,15ch)] flex-1')}
                />

                <label htmlFor='enq-company' className={label}>
                    i&apos;m from
                </label>

                <input
                    id='enq-company'
                    type='text'
                    name='company'
                    value={values.company}
                    onChange={set('company')}
                    placeholder='Company'
                    autoComplete='organization'
                    className={cn(field, 'w-[min(100%,15ch)] flex-1')}
                />
            </div>

            <div data-field className='flex flex-wrap items-baseline gap-x-4 gap-y-3'>
                <label htmlFor='enq-service' className={label}>
                    and I need help with
                </label>

                <input
                    id='enq-service'
                    type='text'
                    name='service'
                    value={values.service}
                    onChange={set('service')}
                    placeholder='Brand, web, art direction…'
                    className={cn(field, 'w-full flex-1')}
                />
            </div>

            <div data-field className='flex flex-col gap-3'>
                <label htmlFor='enq-pitch' className={label}>
                    here&apos;s the Idea:
                </label>

                <textarea
                    id='enq-pitch'
                    name='pitch'
                    required
                    rows={3}
                    value={values.pitch}
                    onChange={set('pitch')}
                    placeholder='A sentence or two about the project…'
                    className={cn(field, 'w-full resize-none leading-[1.5]')}
                />
            </div>

            <div data-field className='flex flex-wrap items-baseline gap-x-4 gap-y-3'>
                <label htmlFor='enq-budget' className={label}>
                    my budget is around
                </label>

                <input
                    id='enq-budget'
                    type='text'
                    name='budget'
                    value={values.budget}
                    onChange={set('budget')}
                    placeholder='$10k'
                    className={cn(field, 'w-[min(100%,10ch)] flex-1')}
                />

                <label htmlFor='enq-start' className={label}>
                    and i&apos;d like to start
                </label>

                <select
                    id='enq-start'
                    name='start'
                    required
                    value={values.start}
                    onChange={set('start')}
                    className={cn(field, 'w-[min(100%,18ch)] flex-1 cursor-pointer', values.start ? 'text-paper' : 'text-ash')}
                >
                    <option value='' disabled>
                        When…
                    </option>

                    <option value='ASAP'>
                        ASAP
                    </option>

                    <option value='Within a month'>
                        Within a month
                    </option>

                    <option value='This quarter'>
                        This quarter
                    </option>

                    <option value='Just exploring'>
                        Just exploring
                    </option>
                </select>
            </div>

            <div data-field className='flex flex-wrap items-baseline gap-x-4 gap-y-3'>
                <label htmlFor='enq-email' className={label}>
                    reach me at
                </label>

                <input
                    id='enq-email'
                    type='email'
                    name='email'
                    required
                    value={values.email}
                    onChange={set('email')}
                    placeholder='you@company.com'
                    autoComplete='email'
                    className={cn(field, 'w-full flex-1')}
                />
            </div>

            {/* Decoy field. Real people never see or fill this. */}
            <input
                type='text'
                name='website'
                value={values.website}
                onChange={set('website')}
                tabIndex={-1}
                autoComplete='off'
                aria-hidden='true'
                className='absolute scale-0'
            />

            <div data-field className='flex flex-wrap items-center justify-between gap-6 pt-2'>
                <p
                    className={cn(
                        't-cond-soft max-w-[44ch] text-[13px] leading-[1.5]',
                        status === 'error' ? 'text-brand' : 'text-ash-soft'
                    )}
                    role='status'
                >
                    {message || (
                        <>
                            By submitting, you agree to our
                            {' '}
                            <Link href='/legal/terms' className='text-paper underline underline-offset-2'>
                                terms
                            </Link>
                            {' '}
                            and
                            {' '}
                            <Link href='/legal/privacy' className='text-paper underline underline-offset-2'>
                                privacy policy.
                            </Link>
                        </>
                    )}
                </p>

                <button
                    type='submit'
                    data-cursor='link'
                    disabled={status === 'sending'}
                    className={cn(
                        'group/roll flex items-center gap-3 rounded-[var(--radius-field)] bg-paper px-6 py-3.5 text-ink transition-opacity duration-500',
                        complete || status === 'done' ? 'opacity-100' : 'opacity-70'
                    )}
                >
                    <RollText
                        label={status === 'sending' ? 'Sending' : status === 'done' ? 'Sent' : 'Start a project'}
                        className='t-cond text-[15px]'
                    />

                    {status === 'done'
                        ? (
                            <svg viewBox='0 0 24 24' className='size-3.5 text-brand' fill='none' aria-hidden='true'>
                                <path d='M4 12.5l5 5L20 6.5' stroke='currentColor' strokeWidth='2.4' />
                            </svg>
                        )
                        : (
                            <svg viewBox='0 0 24 24' className='size-3.5 transition-transform duration-500 group-hover/roll:rotate-45' fill='none' aria-hidden='true'>
                                <path d='M5 19L19 5M19 5H8M19 5v11' stroke='currentColor' strokeWidth='2' />
                            </svg>
                        )}
                </button>
            </div>
        </form>
    )
}
